import { useApolloClient, useMutation, useQuery } from "@vue/apollo-composable";
import { UpsertPreferencesDocument, UserPreferencesDocument } from "~/graphql/generated/graphql";
import { cloneDeep } from "~/helpers/common-helpers";
import { type NamespaceName, type UserPreferences, getNamespace } from "~/helpers/user-preferences-helpers";

const BLOB_ORG_PREFIX = "organizationId_";

export const usePreferencesNamespace = <T extends NamespaceName>(namespaceName: T) => {
  const { client } = useApolloClient();

  const query = useQuery(UserPreferencesDocument, {}, { keepPreviousResult: true });

  const mutation = useMutation(UpsertPreferencesDocument);

  const blob = computed(() => query.result.value?.me.preferences?.blob);

  const orgId = computed(() => {
    const orgId = query.result.value?.me.orgId;

    return orgId ? `${BLOB_ORG_PREFIX}${orgId}` : undefined;
  });

  const preferences = computed<Partial<UserPreferences> | undefined>(() => (orgId.value && blob.value?.[orgId.value]) ?? blob.value);

  const queriesLoading = computed(() => query.loading.value || mutation.loading.value);

  const result = ref<UserPreferences[T]>();

  const resultLoading = ref(false);

  const loading = computed(() => queriesLoading.value || resultLoading.value);

  /** NOTE Sven: Unsafe but can only really be fixed by expanding user preferences serverside */
  const update = async (newNamespacePreferences: UserPreferences[T]) => {
    if (queriesLoading.value || !orgId.value || !result.value || newNamespacePreferences.type !== result.value.type) {
      return;
    }

    const newBlob = { ...cloneDeep(blob.value), [orgId.value]: { ...preferences.value, [namespaceName]: newNamespacePreferences } };

    /** First time we scope to org, it will be existing user prefs. Second time, it is existing + new scoped prefs, and so on.
     * So we remove the other scoped preferences here for the new scoped prefs */
    for (let key in newBlob[orgId.value]) {
      if (key.startsWith(BLOB_ORG_PREFIX)) delete newBlob[orgId.value][key];
    }

    await mutation.mutate({ input: { blob: newBlob } });
  };

  watchEffect(async () => {
    if (queriesLoading.value) return;

    resultLoading.value = true;

    try {
      result.value = (await getNamespace(namespaceName, preferences.value ?? {}, client)) as UserPreferences[T];
    } catch (e) {
      console.error(`Failed to load preferences for namespace: ${namespaceName}`, e);
    } finally {
      resultLoading.value = false;
    }
  });

  return {
    result,
    loading,
    update,
    refetch: () => query.refetch(),
  };
};
